import type { PropsWithChildren } from 'react'
import { useContext } from 'react'
import { Link } from 'react-router'
import AccountButton from 'src/components/Account/AccountButton'
import { CustomHeroTitleWithDescription } from 'src/components/Common/SiteName'
import { GeneratorEntry } from 'src/components/GrowthUnit/GeneratorEntry'
import NotificationButton from 'src/components/Notification/NotificationButton'
import MyCredits from 'src/components/Quota/MyCredits'
import { useMyInfo } from 'src/services/apollo/auth'
import { cx } from 'src/utils/cx'
import { track } from 'src/utils/tracker'
import { AppLogoSlot } from '~/features/Growth/growth-slots/AppLogoSlot'
import { LayoutHeaderBanner } from '../AppLayout'
import { WorkbenchTrackingContext } from './tracking'

function LogoButton() {
  const trackingParams = useContext(WorkbenchTrackingContext)

  return (
    <Link
      to="/"
      className="flex h-full items-center no-underline"
      onClick={() => {
        track('panel_home_select', { scene: trackingParams.scene })
      }}
    >
      <div className="row-start-1 col-start-1 size-7">
        <AppLogoSlot />
      </div>
      <CustomHeroTitleWithDescription
        title={'PixAI'}
        size="small"
        className="ml-2 !mt-0 max-mmd:hidden"
      />
    </Link>
  )
}

/**
 * Header
 */
export function Header({
  className,
  style,
  children,
  menuSlot,
}: PropsWithChildren<StyleProps & { menuSlot: React.ReactNode }>) {
  const { data: me } = useMyInfo()

  /*
   * Render
   */

  if (me) {
    return (
      <HeaderWithUserInfoReady
        className={className}
        style={style}
        menuSlot={menuSlot}
      >
        {children}
      </HeaderWithUserInfoReady>
    )
  } else {
    return (
      <HeaderSkeleton className={className} style={style} menuSlot={menuSlot}>
        {children}
      </HeaderSkeleton>
    )
  }
}

/**
 * MyCredits, GeneratorEntry, NotificationButton show when userinfo is ready
 */
function HeaderWithUserInfoReady({
  className,
  style,
  children,
  menuSlot,
}: PropsWithChildren<StyleProps & { menuSlot: React.ReactNode }>) {
  return (
    <div className={cx('sticky top-0 z-30', className)} style={style}>
      <LayoutHeaderBanner />
      <header className="relative h-12 pr-3 mlg:pr-4 flex items-center gap-2 overflow-auto">
        {menuSlot}
        <LogoButton />
        <div className="flex items-center">{children}</div>
        <div className="flex-1" />
        <MyCredits
          truncateOnMobile
          variant="purple-pink"
          className="relative md:absolute md:right-[50%] md:translate-x-[50%]"
        />
        <GeneratorEntry className="max-mlg:hidden" />
        <NotificationButton />
        <AccountButton />
      </header>
    </div>
  )
}

function HeaderSkeleton({
  className,
  style,
  children,
  menuSlot,
}: PropsWithChildren<StyleProps & { menuSlot: React.ReactNode }>) {
  return (
    <div className={cx('sticky top-0 z-30', className)} style={style}>
      <LayoutHeaderBanner />
      <header className="relative h-12 pr-3 mlg:pr-4 flex items-center gap-2 overflow-auto">
        {menuSlot}
        <LogoButton />
        <div className="flex items-center">{children}</div>
        <div className="flex-1" />
        <AccountButton />
      </header>
    </div>
  )
}
